// @ts-strict-ignore
import { lerp } from './utilities/lerp';

/**
 * Takes a sparse number array and fills in the gaps. Returning a new array filled with numbers.
 *
 * Any gaps will be filled with a linear interpolation between the two surrounding values.
 * Undefined values before the first set value will be filled with the first value.
 * Undefined values after the last set value will be filled with the last value.
 * If there are no set values, the array will be filled with zeros.
 *
 * @param data
 * @returns
 */
export function fillOutArray(data: Array<number | undefined>): number[] {
  const firstValueIndex = data.findIndex((d) => d !== undefined);
  const firstValue = data[firstValueIndex] ?? 0;
  if (firstValueIndex === -1) {
    return data.map((d) => 0);
  }
  let lastValueIndex = firstValueIndex;
  let lastValue = firstValue;
  for (let i = data.length - 1; i > firstValueIndex; i--) {
    const val = data[i];
    if (val !== undefined) {
      lastValueIndex = i;
      lastValue = val;
      break;
    }
  }
  const getNextValueIndex = (index) => {
    for (let i = index + 1; i < data.length; i++) {
      if (data[i] !== undefined) return i;
    }
    return -1;
  };
  let prevValue = firstValue;
  let prevIndex = firstValueIndex;

  let nextIndex = getNextValueIndex(firstValueIndex);
  let nextValue = data[nextIndex];

  return data.map((value, index) => {
    if (index < firstValueIndex) {
      return firstValue;
    }
    if (index >= lastValueIndex) {
      return lastValue;
    }
    if (value !== undefined) {
      prevValue = value;
      prevIndex = index;
      nextIndex = getNextValueIndex(index);
      nextValue = data[nextIndex];
      return value;
    }
    const t = (index - prevIndex) / (nextIndex - prevIndex);
    return lerp(prevValue, nextValue, t);
  });
}
